import Measure from 'react-measure'
import { useState, memo, useRef, useEffect, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import isEqual from 'lodash/isEqual'
import { DarkModeContext } from 'components/App'
import { THEME_MODES } from 'style/materialUISetup'

import { useCreateCacheMap } from '../customHooks'
import getShortCacheMap from './getShortCacheMap'
import { SnakeWrapper, ScrollNotification, TimelineInfo, TimelineTooltip } from './style'
import { createGradient, snakeSettings } from './snakeSettings'

const normalizedTimeline = timeline => {
  const duration = Number(timeline?.DurationSeconds)
  const fileOffset = Number(timeline?.FileOffset)
  const fileLength = Number(timeline?.FileLength)
  const anchors = (timeline?.Anchors || [])
    .map(anchor => ({ offset: Number(anchor.Offset), seconds: Number(anchor.Seconds) }))
    .filter(anchor => Number.isFinite(anchor.offset) && Number.isFinite(anchor.seconds))
    .sort((left, right) => left.offset - right.offset)

  if (
    !Number.isFinite(duration) ||
    duration <= 0 ||
    !Number.isFinite(fileOffset) ||
    !Number.isFinite(fileLength) ||
    fileLength <= 0 ||
    anchors.length < 2
  ) {
    return null
  }

  return {
    ...timeline,
    duration,
    fileOffset,
    fileLength,
    currentOffset: Math.max(0, Math.min(fileLength, Number(timeline.CurrentOffset) || 0)),
    anchors,
  }
}

const interpolateTimelineTime = (timeline, offset) => {
  if (!timeline) return null
  const clampedOffset = Math.max(0, Math.min(timeline.fileLength, Number(offset) || 0))
  const { anchors } = timeline

  let low = 0
  let high = anchors.length - 1
  while (low + 1 < high) {
    const middle = Math.floor((low + high) / 2)
    if (anchors[middle].offset <= clampedOffset) low = middle
    else high = middle
  }

  const left = anchors[low]
  const right = anchors[high]
  if (clampedOffset <= left.offset || right.offset <= left.offset) return left.seconds
  if (clampedOffset >= right.offset) return right.seconds

  const ratio = (clampedOffset - left.offset) / (right.offset - left.offset)
  return left.seconds + ratio * (right.seconds - left.seconds)
}

const formatTimelineTime = value => {
  const totalSeconds = Math.max(0, Math.round(Number(value) || 0))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const minuteText = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  const time = `${minuteText}:${String(seconds).padStart(2, '0')}`
  return hours > 0 ? `${hours}:${time}` : time
}

const pieceTimelineRange = (timeline, pieceLength, pieceID) => {
  if (!timeline || !pieceLength || !Number.isFinite(pieceID)) return null
  const pieceStart = pieceID * pieceLength - timeline.fileOffset
  const pieceEnd = pieceStart + pieceLength
  if (pieceEnd <= 0 || pieceStart >= timeline.fileLength) return null

  const startOffset = Math.max(0, pieceStart)
  const endOffset = Math.min(timeline.fileLength, pieceEnd)
  return {
    start: interpolateTimelineTime(timeline, startOffset),
    end: interpolateTimelineTime(timeline, endOffset),
  }
}

const TorrentCache = ({ cache, isMini, preloadSize, isSnakeDebugMode }) => {
  const { t } = useTranslation()
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const { width } = dimensions
  const canvasRef = useRef(null)
  const ctxRef = useRef(null)
  const cacheMap = useCreateCacheMap(cache)
  const timeline = useMemo(() => normalizedTimeline(cache.MediaTimeline), [cache.MediaTimeline])
  const settingsTarget = isMini ? 'mini' : 'default'
  const { isDarkMode } = useContext(DarkModeContext)
  const theme = isDarkMode ? THEME_MODES.DARK : THEME_MODES.LIGHT

  const {
    readerColor,
    readerOutlineColor,
    rangeColor,
    borderWidth,
    pieceSize,
    gapBetweenPieces,
    backgroundColor,
    borderColor,
    cacheMaxHeight,
    completeColor,
    timelineColor,
  } = snakeSettings[theme][settingsTarget]

  const canvasWidth = isMini ? width * 0.93 : width

  const pieceSizeWithGap = pieceSize + gapBetweenPieces
  // Reserve the time gutters for the full map even before media metadata is
  // available. This keeps the number of columns and every block position
  // stable when the timeline appears or is temporarily unavailable.
  const timelineGutter = !isMini ? (canvasWidth >= 360 ? 48 : 34) : 0
  const gridWidth = Math.max(pieceSizeWithGap, canvasWidth - timelineGutter * 2)
  const piecesInOneRow = Math.max(1, Math.floor(gridWidth / pieceSizeWithGap))

  let shotCacheMap
  if (isMini) {
    const pieceLength = Number(cache.PiecesLength) || 0
    const preferredSize = Number(preloadSize) > 0 ? Number(preloadSize) : Number(cache.Capacity) || 0
    const requestedPiecesAmount = pieceLength > 0 ? Math.max(1, Math.ceil(preferredSize / pieceLength)) : 1
    const torrentPiecesAmount = Number(cache.PiecesCount) || 0
    const preloadPiecesAmount =
      torrentPiecesAmount > 0 ? Math.min(requestedPiecesAmount, torrentPiecesAmount) : requestedPiecesAmount

    shotCacheMap = getShortCacheMap({
      cacheMap,
      preloadPiecesAmount,
      piecesInOneRow,
    })
  }
  const source = isMini ? shotCacheMap : cacheMap
  const startingXPoint = timelineGutter + Math.ceil((gridWidth - pieceSizeWithGap * piecesInOneRow) / 2) // needed to center grid
  const height = Math.ceil(source.length / piecesInOneRow) * pieceSizeWithGap
  const pieceLength = Number(cache.PiecesLength) || 0
  const currentTimelineTime = timeline ? interpolateTimelineTime(timeline, timeline.currentOffset) : null
  const timelineReaderPiece =
    timeline && pieceLength > 0
      ? Math.floor((timeline.fileOffset + Math.min(timeline.currentOffset, timeline.fileLength - 1)) / pieceLength)
      : null
  const [timelineTooltip, setTimelineTooltip] = useState(null)

  useEffect(() => {
    if (!canvasWidth || !height) return

    const canvas = canvasRef.current
    canvas.width = canvasWidth
    canvas.height = height
    ctxRef.current = canvas.getContext('2d')
  }, [canvasRef, height, canvasWidth])

  useEffect(() => {
    const ctx = ctxRef.current
    if (!ctx) return

    ctx.clearRect(0, 0, canvasWidth, height)

    source.forEach(({ id, percentage, priority, isReader, isReaderRange }, i) => {
      const isCurrentReader = isReader || (!isMini && id === timelineReaderPiece)
      const inProgress = percentage > 0 && percentage < 100
      const isCompleted = percentage === 100
      const currentRow = i % piecesInOneRow
      const currentColumn = Math.floor(i / piecesInOneRow)
      const fixBlurStroke = borderWidth % 2 === 0 ? 0 : 0.5
      const requiredFix = Math.ceil(borderWidth / 2) + 1 + fixBlurStroke
      const x = currentRow * pieceSize + currentRow * gapBetweenPieces + startingXPoint + requiredFix
      const y = currentColumn * pieceSize + currentColumn * gapBetweenPieces + requiredFix

      ctx.lineWidth = borderWidth
      ctx.fillStyle = inProgress
        ? createGradient(ctx, percentage, theme, settingsTarget)
        : isCompleted
        ? completeColor
        : backgroundColor
      ctx.strokeStyle = isCurrentReader
        ? readerColor
        : inProgress || isCompleted
        ? completeColor
        : isReaderRange
        ? rangeColor
        : borderColor

      ctx.translate(x, y)
      ctx.fillRect(0, 0, pieceSize, pieceSize)
      ctx.strokeRect(0, 0, pieceSize, pieceSize)
      ctx.setTransform(1, 0, 0, 1, 0, 0)

      if (isSnakeDebugMode && priority > 0) {
        let info = ''
        if (priority === 1) info = ''
        else if (priority === 2) info = 'H'
        else if (priority === 3) info = 'R'
        else if (priority === 4) info = 'N'
        else if (priority === 5) info = 'A'
        ctx.font = isMini ? '13px monospace' : '10px monospace'
        const xpad = isMini ? pieceSize * 0.35 : pieceSize * 0.29
        const ypad = isMini ? pieceSize * 0.69 : pieceSize * 0.78
        ctx.fillStyle = 'black'
        ctx.fillText(info, x + xpad, y + ypad)
      }

      // Draw the playback reader last so cache progress and debug priority
      // letters can never cover the current streamed piece.
      if (isCurrentReader) {
        const markerInset = isMini ? 3 : 2
        const markerSize = pieceSize - markerInset * 2
        ctx.save()
        ctx.lineJoin = 'miter'
        ctx.strokeStyle = readerOutlineColor
        ctx.lineWidth = isMini ? 5 : 4
        ctx.strokeRect(x + markerInset, y + markerInset, markerSize, markerSize)
        ctx.strokeStyle = readerColor
        ctx.lineWidth = isMini ? 2.5 : 2
        ctx.strokeRect(x + markerInset, y + markerInset, markerSize, markerSize)
        ctx.restore()
      }
    })

    if (timeline && !isMini && pieceLength > 0) {
      const rows = Math.ceil(source.length / piecesInOneRow)
      ctx.save()
      ctx.fillStyle = timelineColor
      ctx.font = canvasWidth >= 600 ? '10px monospace' : '9px monospace'
      ctx.textBaseline = 'middle'

      for (let row = 0; row < rows; row++) {
        const first = source[row * piecesInOneRow]
        const last = source[Math.min(source.length - 1, (row + 1) * piecesInOneRow - 1)]
        if (first && last && Number.isFinite(first.id) && Number.isFinite(last.id)) {
          const rowStartOffset = first.id * pieceLength - timeline.fileOffset
          const rowEndOffset = (last.id + 1) * pieceLength - timeline.fileOffset
          const overlapsSelectedFile = rowEndOffset > 0 && rowStartOffset < timeline.fileLength

          if (overlapsSelectedFile) {
            const rowY = row * pieceSizeWithGap + Math.ceil(pieceSize / 2) + 1
            const startTime = interpolateTimelineTime(timeline, Math.max(0, rowStartOffset))
            const endTime = interpolateTimelineTime(timeline, Math.min(timeline.fileLength, rowEndOffset))
            ctx.textAlign = 'right'
            ctx.fillText(formatTimelineTime(startTime), startingXPoint - 5, rowY)
            ctx.textAlign = 'left'
            ctx.fillText(formatTimelineTime(endTime), startingXPoint + piecesInOneRow * pieceSizeWithGap + 2, rowY)
          }
        }
      }
      ctx.restore()
    }
  }, [
    cacheMap,
    height,
    canvasWidth,
    piecesInOneRow,
    startingXPoint,
    pieceSize,
    gapBetweenPieces,
    source,
    backgroundColor,
    borderColor,
    borderWidth,
    settingsTarget,
    completeColor,
    readerColor,
    readerOutlineColor,
    rangeColor,
    isMini,
    theme,
    isSnakeDebugMode,
    timeline,
    timelineColor,
    pieceLength,
    pieceSizeWithGap,
    timelineReaderPiece,
  ])

  const handleTimelineMouseMove = event => {
    if (!timeline || isMini || !pieceLength) return

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const x = ((event.clientX - rect.left) * canvas.width) / rect.width
    const y = ((event.clientY - rect.top) * canvas.height) / rect.height
    const column = Math.floor((x - startingXPoint) / pieceSizeWithGap)
    const row = Math.floor(y / pieceSizeWithGap)
    if (column < 0 || column >= piecesInOneRow || row < 0) {
      setTimelineTooltip(null)
      return
    }

    const localX = x - startingXPoint - column * pieceSizeWithGap
    const localY = y - row * pieceSizeWithGap
    const sourceIndex = row * piecesInOneRow + column
    const item = source[sourceIndex]
    if (!item || localX > pieceSize || localY > pieceSize) {
      setTimelineTooltip(null)
      return
    }

    const range = pieceTimelineRange(timeline, pieceLength, item.id)
    if (!range) {
      setTimelineTooltip(null)
      return
    }

    setTimelineTooltip({
      x,
      y,
      text: `≈ ${formatTimelineTime(range.start)} — ${formatTimelineTime(range.end)}`,
    })
  }

  return (
    <Measure bounds onResize={({ bounds }) => setDimensions(bounds)}>
      {({ measureRef }) => (
        <div style={{ display: 'flex', flexDirection: 'column' }} ref={measureRef}>
          <SnakeWrapper themeType={theme} isMini={isMini}>
            <canvas
              ref={canvasRef}
              onMouseMove={handleTimelineMouseMove}
              onMouseLeave={() => setTimelineTooltip(null)}
            />
            {timelineTooltip && (
              <TimelineTooltip style={{ left: timelineTooltip.x, top: timelineTooltip.y }} themeType={theme}>
                {timelineTooltip.text}
              </TimelineTooltip>
            )}
          </SnakeWrapper>

          {!isMini && timeline && (
            <TimelineInfo themeType={theme}>
              <span>{t('StreamPosition', { time: formatTimelineTime(currentTimelineTime) })}</span>
              <span>{timeline.Accuracy === 'keyframes' ? t('TimelineKeyframes') : t('TimelineEstimated')}</span>
            </TimelineInfo>
          )}
          {isMini && height >= cacheMaxHeight && <ScrollNotification>{t('ScrollDown')}</ScrollNotification>}
        </div>
      )}
    </Measure>
  )
}

export default memo(
  TorrentCache,
  (prev, next) =>
    isEqual(prev.cache.Pieces, next.cache.Pieces) &&
    isEqual(prev.cache.Readers, next.cache.Readers) &&
    isEqual(prev.cache.MediaTimeline, next.cache.MediaTimeline),
)
